/** The RoleId backing the "Guest" role. */
export const GUEST_ROLE_ID = 'guest';

/** Email domain that marks a user as a USDA (non-guest) account. */
export const USDA_EMAIL_DOMAIN = '@usda.gov';

/** Subset of person fields the email-based guest check understands. */
export interface GuestEmailPerson {
  isUSDAUser?: boolean | null;
  users?: { email?: string | null }[] | null;
}

function hasUsdaEmail(email?: string | null): boolean {
  return !!email && email.trim().toLowerCase().endsWith(USDA_EMAIL_DOMAIN);
}

/**
 * Whether a person should show the guest badge, decided by email: a person is
 * treated as a "guest" when they do NOT have a usda.gov email address on their
 * profile.
 *
 * Resolution order:
 *  1. The backend `isUSDAUser` virtual (presence of any usda.gov email) when
 *     present — `guest = !isUSDAUser`.
 *  2. Otherwise scan the `users` array for a usda.gov email.
 *
 * Returns `undefined` when the object carries neither signal (e.g. a slim
 * `{ id }` badgePerson), so the caller can resolve by id via
 * {@link GuestRoleService} instead.
 */
export function personIsGuest(
  person: GuestEmailPerson | null | undefined
): boolean | undefined {
  if (person == null) {
    return undefined;
  }
  if (typeof person.isUSDAUser === 'boolean') {
    return !person.isUSDAUser;
  }
  if (Array.isArray(person.users)) {
    return !person.users.some((user) => hasUsdaEmail(user?.email));
  }
  return undefined;
}

/** Crew row shape needed to validate the guest POC requirement on checkout. */
export interface GuestPocCrewRow {
  crewMember?: GuestEmailPerson | null;
  supervisorId?: string | null;
  supervisor?: { id?: string | null } | null;
  secondSupervisorId?: string | null;
  secondSupervisor?: { id?: string | null } | null;
}

/**
 * A self-assigned guest is added to a trip as a Crew Member with no Point of
 * Contact, so the crew form's "POC required" validator never runs for them.
 *
 * Returns false when any guest crew row is missing a POC (supervisor); the
 * Additional POC (secondSupervisor) is optional. Non-guest rows are ignored.
 * Use this to gate the checkout button. Mirrors the backend guard in
 * okay.controller.js (requireGuestPocForCheckout), which is the authoritative
 * enforcement.
 */
export function guestCrewHaveRequiredPoc(
  crewMembers: GuestPocCrewRow[] | null | undefined
): boolean {
  return (crewMembers ?? []).every((meta) => {
    if (personIsGuest(meta.crewMember) !== true) {
      return true;
    }
    return !!(meta.supervisorId || meta.supervisor?.id);
  });
}

/**
 * A person is treated as a "guest" (for the purpose of showing the guest
 * badge) when they have the guest role assigned in *any* team - even if they
 * also hold non-guest roles (e.g. team_member) elsewhere.
 *
 * This matches the AC literally ("Icon appears only for users with the Guest
 * role") and accounts for the email-swap auto-upgrade workflow, which leaves
 * formerly guest-only people with mixed roles once they're added to teams.
 *
 * Use this when the object already carries its `AssignedRoles` (people table,
 * crew-member grid, team member lists). For slim objects that lack role data
 * (dropdowns, trips table) use {@link GuestRoleService} instead.
 */
export function personHasGuestRole(person: {
  AssignedRoles?: { RoleId: string }[];
}): boolean {
  const roles = person?.AssignedRoles;
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => role.RoleId === GUEST_ROLE_ID);
}
